import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { useSaveStripeMapping } from "@/entities/stripeCheckout/hooks";
import { ContractMapping } from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import { PricesDialog } from "@/pages/settings/components/integrations/stripeCheckout/PricesDialog";
import {
  STRIPE_CHECKOUT_PREFIX,
  priceRows
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import { useServiceItemOptions } from "@/pages/settings/components/integrations/stripeCheckout/useServiceItemOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.prices`;

interface PricesSectionProps {
  mapping: ContractMapping | undefined;
  isLoading: boolean;
  subTitle: string;
  isBusy: boolean;
}

// Which service item, and for how long, each Stripe price buys.
export const PricesSection: React.FC<PricesSectionProps> = ({
  mapping,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const save = useSaveStripeMapping();
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rows = useMemo(() => (mapping ? priceRows(mapping) : []), [mapping]);
  const savedIds = useMemo(
    () => (mapping?.services ?? []).map((service) => service.serviceItemId),
    [mapping]
  );
  const serviceItems = useServiceItemOptions(
    mapping?.productId ?? null,
    savedIds
  );

  const describe = (row: (typeof rows)[number]) => {
    const parts = [
      serviceItems.nameOf(row.serviceItemId),
      row.durationCount
        ? t(`${PREFIX}.duration.${row.durationUnit}`, {
            count: Number(row.durationCount)
          })
        : "",
      row.fields.length > 0
        ? t(`${PREFIX}.fieldCount`, { count: row.fields.length })
        : ""
    ];
    return parts.filter(Boolean).join(" · ");
  };

  const saveMapping = (edited: ContractMapping) => {
    setSaveError(null);
    save.mutate(
      { data: { mapping: edited } },
      {
        onSuccess: () => {
          setIsEditing(false);
          toast.success(t(`${PREFIX}.saved`));
        },
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  const sectionSubTitle = serviceItems.isError
    ? t(`${PREFIX}.catalogLoadError`)
    : subTitle;

  return (
    <>
      <Section
        title={t(`${PREFIX}.title`)}
        subTitle={sectionSubTitle}
        isLoading={isLoading}
        action={
          <Button
            variant="secondary"
            data-test="stripe-checkout-prices-edit"
            onClick={() => {
              setSaveError(null);
              setIsEditing(true);
            }}
            disabled={!mapping || isBusy || save.isPending}
          >
            {t(`${PREFIX}.edit`)}
          </Button>
        }
        items={rows.map((row) => ({
          title: row.plan,
          subTitle: describe(row)
        }))}
      >
        {mapping && rows.length === 0 && (
          <Text
            variant="bodySecondary"
            data-test="stripe-checkout-prices-empty"
          >
            {t(`${PREFIX}.empty`)}
          </Text>
        )}
      </Section>
      {isEditing && mapping && (
        <PricesDialog
          mapping={mapping}
          serviceItemOptions={serviceItems.options}
          isCatalogLoading={serviceItems.isLoading}
          isSaving={save.isPending}
          saveError={saveError}
          onSave={saveMapping}
          onClose={() => setIsEditing(false)}
        />
      )}
    </>
  );
};
